import React, { useState, useEffect } from 'react';
import { Link, useParams } from 'react-router-dom';
import { MagnifyingGlassIcon, Bars3Icon } from '@heroicons/react/24/outline';
import { Swiper, SwiperSlide } from 'swiper/react';
import { Autoplay, Pagination } from 'swiper/modules';
import axios from 'axios';
import 'swiper/css';
import 'swiper/css/pagination';
import './css/sidesection.css';
import { useLanguage } from '../context/LanguageContext';
import { useTranslation } from '../hooks/useTranslation';
import { API_URL, getUploadUrl } from '../config/api';

function SideSection({ className }) {
  const { toggleLanguage, language } = useLanguage();
  const { t } = useTranslation();
  const [heroImages, setHeroImages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [categoryBanner, setCategoryBanner] = useState(null);
  const { category } = useParams();
  
  useEffect(() => {
    fetchHeroImages();
  }, []);

  // Fetch category banner when a category is selected (category/product pages)
  useEffect(() => {
    const fetchCategoryBanner = async () => {
      if (!category) {
        setCategoryBanner(null);
        return;
      }

      try {
        const response = await axios.get(`${API_URL}/api/categories`);
        const foundCategory = response.data.find((cat) => cat.key_name === category);
        if (foundCategory && foundCategory.image) {
          setCategoryBanner(getUploadUrl(foundCategory.image));
        } else {
          setCategoryBanner(null);
        }
      } catch (error) {
        console.error('Error fetching category banner:', error);
        setCategoryBanner(null);
      }
    };

    fetchCategoryBanner();
  }, [category]);

  const fetchHeroImages = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/hero-images`);
      setHeroImages(response.data);
    } catch (error) {
      console.error('Error fetching hero images:', error);
      // Fallback to empty array if API fails
      setHeroImages([]);
    } finally {
      setLoading(false);
    }
  };

  const sectionClassName = className ? `side-section ${className}` : 'side-section';

  return (
    <section className={sectionClassName}>

        <div className="images-slider">
            {categoryBanner ? (
                <div className="category-banner">
                    <img src={categoryBanner} alt="Category banner" className="category-banner-image" />
                </div>
            ) : loading ? (
                <div className="loading-hero-images">
                    <p>Loading...</p>
                </div>
            ) : heroImages.length > 0 ? (
                <Swiper
                    modules={[Autoplay, Pagination]}
                    spaceBetween={0}
                    slidesPerView={1}
                    loop={heroImages.length > 1}
                    autoplay={{
                        delay: 5000,
                        disableOnInteraction: false,
                    }}
                    pagination={{
                        clickable: true,
                    }}
                    className="swiper-container"
                >
                    {heroImages.map((heroImage) => {
                        const imageUrl = getUploadUrl(heroImage.image);
                        const title = language === 'ar' ? heroImage.title_ar : heroImage.title_en;
                        
                        return (
                            <SwiperSlide key={heroImage.id}>
                                <div className="slide-item">
                                    {heroImage.link_url ? (
                                        <a href={heroImage.link_url} target="_blank" rel="noopener noreferrer">
                                            <img 
                                                src={imageUrl} 
                                                alt={title || `Hero Image ${heroImage.id}`} 
                                                className='hero-image' 
                                            />
                                        </a>
                                    ) : (
                                        <img 
                                            src={imageUrl} 
                                            alt={title || `Hero Image ${heroImage.id}`} 
                                            className='hero-image' 
                                        />
                                    )}
                                </div>
                            </SwiperSlide>
                        );
                    })}
                </Swiper>
            ) : (
                <div className="no-hero-images">
                    <p>No images available</p>
                </div>
            )}
        </div>

            <div className='side-section-nav'>
                <div className='lang-switcher'>
                    <button onClick={toggleLanguage} type="button">
                        {language === 'ar' ? 'EN' : 'AR'}
                    </button>
                </div>

                <div className='search-btn'>
                    <Link to="/search">
                        <button><MagnifyingGlassIcon className='icon' /></button>
                    </Link>
                </div>

                <div className="open-sidenav-btn">
                <Link to="/">

                    <button><Bars3Icon className='icon' /></button>
                    </Link>

                </div>
            </div>
    </section>
  )
}

export default SideSection